import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as EditIcon } from '../../assets/icons/edit-pencil.svg';
import { CardPool, LedgerEntry, RfidToken } from '../../interface/rfid-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchCardPoolByIdApi,
  fetchCardPoolsApi,
  setSelectedCardPool,
} from '../../redux/slices/CardGroupSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  fetchRfidSummaryApi,
  fetchTokenByIdApi,
  fetchTokensApi,
  setSelectedToken,
} from '../../redux/slices/RfidTokenSlice';
import { fetchLedgerByIdApi, fetchLedgerEntriesApi } from '../../redux/slices/TapCardLedgerSlice';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import TabToggle from '../../shared-components/tab-toggle/TabToggle';

import AddCardModal from './components/AddCardModal';
import AddGroupModal from './components/AddGroupModal';
import CardGroupsList from './components/CardGroupsList';
import DeactivateCardModal from './components/DeactivateCardModal';
import GroupDetailsModal from './components/GroupDetailsModal';
import LedgerDetailsModal from './components/LedgerDetailsModal';
import LedgerEntriesList from './components/LedgerEntriesList';
import RechargeGroupModal from './components/RechargeGroupModal';
import RfidStatsOverview from './components/RfidStatsOverview';
import RfidTableActions from './components/RfidTableActions';
import TokenDetailsModal from './components/TokenDetailsModal';
import TokensList from './components/TokensList';

/**
 * RFID and Tap Card Management main page component
 * @returns {React.JSX.Element} RFID management page with custom table list views and actions
 */
function RFIDManagement(): React.JSX.Element {
  const { t, i18n } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [, setSearchParameters] = useSearchParams();
  const {
    tokens,
    totalResults: tokensTotal,
    page: tokensPage,
    limit: tokensLimit,
  } = useAppSelector((state) => state.rfidTokens);
  const {
    cardPools,
    totalResults: cardPoolsTotal,
    page: cardPoolsPage,
    limit: cardPoolsLimit,
  } = useAppSelector((state) => state.cardGroups);
  const {
    ledgerEntries,
    totalResults: ledgerTotal,
    page: ledgerPage,
    limit: ledgerLimit,
  } = useAppSelector((state) => state.tapCardLedger);

  // Table side panel states
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelType, setSidePanelType] = useState<
    'add-card' | 'add-group' | 'group-details' | 'token-details' | 'ledger-details' | null
  >(null);
  const [sidePanelRefreshKey, setSidePanelRefreshKey] = useState<number>(0);
  const [isGroupEditing, setIsGroupEditing] = useState<boolean>(false);
  const [isTokenEditing, setIsTokenEditing] = useState<boolean>(false);
  const [activeToken, setActiveToken] = useState<RfidToken | null>(null);
  const [activeGroup, setActiveGroup] = useState<CardPool | null>(null);
  const [activeLedgerEntry, setActiveLedgerEntry] = useState<LedgerEntry | null>(null);
  const [rechargeGroup, setRechargeGroup] = useState<CardPool | null>(null);
  const [deactivateTargetToken, setDeactivateTargetToken] = useState<RfidToken | null>(null);
  const [successModalData, setSuccessModalData] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  /**
   * Opens the add card table side panel
   * @returns {void}
   */
  const handleOpenAddCard = (): void => {
    setSidePanelType('add-card');
    setSidePanelOpen(true);
  };

  /**
   * Opens the add card group table side panel
   * @returns {void}
   */
  const handleOpenAddGroup = (): void => {
    setSidePanelType('add-group');
    setSidePanelOpen(true);
  };

  /**
   * Closes the active table side panel
   * @returns {void}
   */
  const handleCloseSidePanel = (): void => {
    setSidePanelOpen(false);
    setSidePanelType(null);
    setIsGroupEditing(false);
    setIsTokenEditing(false);
    setActiveToken(null);
    setActiveGroup(null);
    setActiveLedgerEntry(null);
  };

  /**
   * Handles successful card registration and triggers success modal
   * @returns {void}
   */
  const handleCardSuccess = (): void => {
    handleCloseSidePanel();
    setSuccessModalData({
      open: true,
      title: t('cardCreatedSuccess', { defaultValue: 'RFID Card Registered Successfully!' }),
      description: t('cardCreatedSuccessDesc', {
        defaultValue: 'The physical RFID card has been registered and is now active.',
      }),
    });
    dispatch(fetchTokensApi({ page: tokensPage, limit: tokensLimit }));
    dispatch(fetchRfidSummaryApi());
  };

  /**
   * Handles successful card group creation and triggers success modal
   * @returns {void}
   */
  const handleGroupSuccess = (): void => {
    handleCloseSidePanel();
    setSuccessModalData({
      open: true,
      title: t('groupCreatedSuccess', { defaultValue: 'Card Group Created Successfully!' }),
      description: t('groupCreatedSuccessDesc', {
        defaultValue: 'The new Card Group balance has been registered and is now available.',
      }),
    });
    dispatch(fetchCardPoolsApi({ page: cardPoolsPage, limit: cardPoolsLimit }));
    dispatch(fetchRfidSummaryApi());
  };

  /**
   * Opens the recharge group modal
   * @param {CardPool} group - Target card group
   * @returns {void}
   */
  const handleOpenRecharge = (group: CardPool): void => {
    dispatch(setSelectedCardPool(group));
    setRechargeGroup(group);
  };

  /**
   * Handles successful card group recharge and triggers success modal
   * @param {number} [amountKwh] - Credited energy amount in kWh
   * @returns {void}
   */
  const handleRechargeSuccess = (amountKwh?: number): void => {
    const isIndividualRecharge = rechargeGroup?.kind === 'INDIVIDUAL';
    setRechargeGroup(null);
    setSuccessModalData({
      open: true,
      title: isIndividualRecharge
        ? t('cardRechargedSuccess', { defaultValue: 'RFID Card Recharged Successfully!' })
        : t('rechargeSuccess', { defaultValue: 'Card Group Recharged Successfully!' }),
      description: amountKwh
        ? t(isIndividualRecharge ? 'cardRechargeSuccessAmountDesc' : 'rechargeSuccessAmountDesc', {
            amountKwh,
            defaultValue: isIndividualRecharge
              ? 'RFID Card balance has been credited with {{amountKwh}} kWh.'
              : 'Card Group balance has been credited with {{amountKwh}} kWh.',
          })
        : t(isIndividualRecharge ? 'cardRechargeSuccessDesc' : 'rechargeSuccessDesc', {
            defaultValue: isIndividualRecharge
              ? 'RFID Card balance has been credited successfully.'
              : 'Card Group balance has been credited successfully.',
          }),
    });
    setSidePanelRefreshKey((previous) => previous + 1);
    if (sidePanelType === 'token-details' && activeToken) {
      const tokenId = activeToken._id || activeToken.id;
      if (tokenId) {
        dispatch(fetchTokenByIdApi(tokenId));
      }
    }
    if (sidePanelType === 'group-details' && activeGroup) {
      const groupId = activeGroup._id || activeGroup.id;
      if (groupId) {
        dispatch(fetchCardPoolByIdApi(groupId));
      }
    }
    dispatch(fetchTokensApi({ page: tokensPage, limit: tokensLimit }));
    dispatch(fetchCardPoolsApi({ page: cardPoolsPage, limit: cardPoolsLimit }));
    dispatch(fetchLedgerEntriesApi({ page: ledgerPage, limit: ledgerLimit }));
    dispatch(fetchRfidSummaryApi());
  };

  /**
   * Handles successful card deactivation and triggers success modal
   * @returns {void}
   */
  const handleDeactivateSuccess = (): void => {
    setDeactivateTargetToken(null);
    handleCloseSidePanel();
    setSearchParameters(
      (previous) => {
        const parameters = new URLSearchParams(previous);
        parameters.delete('action');
        parameters.delete('tokenId');
        return parameters;
      },
      { replace: true },
    );
    setSuccessModalData({
      open: true,
      title: t('cardDeactivatedSuccess', { defaultValue: 'RFID Card Deactivated' }),
      description: t('cardDeactivatedSuccessDesc', {
        defaultValue: 'The physical RFID card has been permanently deactivated.',
      }),
    });
    dispatch(fetchTokensApi({ page: tokensPage, limit: tokensLimit }));
    dispatch(fetchCardPoolsApi({ page: cardPoolsPage, limit: cardPoolsLimit }));
    dispatch(fetchLedgerEntriesApi({ page: ledgerPage, limit: ledgerLimit }));
    dispatch(fetchRfidSummaryApi());
  };

  /**
   * Opens the deactivate card modal
   * @param {RfidToken} token - Target RFID token
   * @returns {void}
   */
  const handleOpenDeactivate = (token: RfidToken): void => {
    setDeactivateTargetToken(token);
  };

  /**
   * Opens the token details table side panel
   * @param {RfidToken} token - Target RFID token
   * @param {boolean} [isEdit] - Whether to open directly in edit mode
   * @returns {void}
   */
  const handleOpenTokenDetails = (token: RfidToken, isEdit = false): void => {
    const tokenId = token._id || token.id;
    setIsTokenEditing(isEdit);
    dispatch(setSelectedToken(token));
    setActiveToken(token);
    setSidePanelType('token-details');
    setSidePanelOpen(true);
    if (tokenId) {
      dispatch(fetchTokenByIdApi(tokenId));
    }
  };

  /**
   * Opens the token edit table side panel
   * @param {RfidToken} token - Target RFID token
   * @returns {void}
   */
  const handleOpenEditToken = (token: RfidToken): void => {
    handleOpenTokenDetails(token, true);
  };

  /**
   * Opens the card group details table side panel
   * @param {CardPool} group - Target card group
   * @param {boolean} [isEdit] - Whether to open directly in edit mode
   * @returns {void}
   */
  const handleOpenGroupDetails = (group: CardPool, isEdit = false): void => {
    const groupId = group._id || group.id;
    setIsGroupEditing(isEdit);
    dispatch(setSelectedCardPool(group));
    setActiveGroup(group);
    setSidePanelType('group-details');
    setSidePanelOpen(true);
    if (groupId) {
      dispatch(fetchCardPoolByIdApi(groupId));
    }
  };

  /**
   * Opens the card group edit table side panel
   * @param {CardPool} group - Target card group
   * @returns {void}
   */
  const handleOpenEditGroup = (group: CardPool): void => {
    handleOpenGroupDetails(group, true);
  };

  /**
   * Opens the ledger entry details table side panel
   * @param {LedgerEntry} entry - Target ledger entry
   * @returns {void}
   */
  const handleOpenLedgerDetails = (entry: LedgerEntry): void => {
    const ledgerId = entry._id || entry.id;
    setActiveLedgerEntry(entry);
    setSidePanelType('ledger-details');
    setSidePanelOpen(true);
    if (ledgerId) {
      dispatch(fetchLedgerByIdApi(ledgerId));
    }
  };

  useEffect(() => {
    dispatch(setPageTitle(t('rfidCardsManagement', { defaultValue: 'RFID Cards Management' })));
    dispatch(fetchTokensApi({}));
    dispatch(fetchCardPoolsApi({}));
    dispatch(fetchLedgerEntriesApi({}));
    dispatch(fetchRfidSummaryApi());
  }, [dispatch, t]);

  const tabs = [
    {
      label: t('rfidCardsTab', { defaultValue: 'RFID Cards (Tokens)' }),
      value: '0',
      count: tokensTotal || tokens.length,
    },
    {
      label: t('cardGroupsTab', { defaultValue: 'Card Groups' }),
      value: '1',
      count: cardPoolsTotal || cardPools.length,
    },
    {
      label: t('ledgerEntriesTab', { defaultValue: 'Business Tap Ledger' }),
      value: '2',
      count: ledgerTotal || ledgerEntries.length,
    },
  ];

  /**
   * Renders the header title and action icons for the active table side panel
   * @returns {React.JSX.Element | undefined} Side panel header title element
   */
  const renderSidePanelTopLeftContent = (): React.JSX.Element | undefined => {
    if (sidePanelType === 'add-card') {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('registerNewCard', { defaultValue: 'Register New RFID Card' })}
          </p>
        </div>
      );
    }
    if (sidePanelType === 'add-group') {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('createNewGroup', { defaultValue: 'Create New Card Group' })}
          </p>
        </div>
      );
    }
    if (sidePanelType === 'token-details') {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isTokenEditing
              ? t('editCard', { defaultValue: 'Edit RFID Card' })
              : t('cardDetails', { defaultValue: 'RFID Card Details' })}
          </p>
          <div className="side-panel-header-left-icons">
            {!isTokenEditing && activeToken?.status === 'ACTIVE' && (
              <div className="button-wrap">
                <span
                  role="presentation"
                  className="access-facility"
                  onClick={() => setIsTokenEditing(true)}>
                  <EditIcon className="icon" />
                </span>
                <div className="hover-label">{t('edit', { defaultValue: 'Edit' })}</div>
              </div>
            )}
          </div>
        </div>
      );
    }
    if (sidePanelType === 'group-details') {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isGroupEditing
              ? t('editCardGroup', { defaultValue: 'Edit Card Group' })
              : t('groupDetails', { defaultValue: 'Card Group Details' })}
          </p>
          <div className="side-panel-header-left-icons">
            {!isGroupEditing && (
              <div className="button-wrap">
                <span
                  role="presentation"
                  className="access-facility"
                  onClick={() => setIsGroupEditing(true)}>
                  <EditIcon className="icon" />
                </span>
                <div className="hover-label">{t('edit', { defaultValue: 'Edit' })}</div>
              </div>
            )}
          </div>
        </div>
      );
    }
    if (sidePanelType === 'ledger-details') {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {t('ledgerEntryDetails', { defaultValue: 'Ledger Entry Details' })}
          </p>
        </div>
      );
    }
    return undefined;
  };

  /**
   * Renders the form content for the active table side panel
   * @returns {React.JSX.Element | undefined} Side panel form content element
   */
  const renderSidePanelManualContent = (): React.JSX.Element | undefined => {
    if (sidePanelType === 'add-card') {
      return (
        <AddCardModal
          key="add-card-panel"
          onClose={handleCloseSidePanel}
          onSuccess={handleCardSuccess}
        />
      );
    }
    if (sidePanelType === 'add-group') {
      return (
        <AddGroupModal
          key="add-group-panel"
          onClose={handleCloseSidePanel}
          onSuccess={handleGroupSuccess}
        />
      );
    }
    if (sidePanelType === 'token-details' && activeToken) {
      const tokenId = activeToken._id || activeToken.id;
      return (
        <TokenDetailsModal
          key={`token-details-${tokenId}-${sidePanelRefreshKey}`}
          token={activeToken}
          tokenId={tokenId}
          isEditing={isTokenEditing}
          onEditModeChange={setIsTokenEditing}
          onClose={handleCloseSidePanel}
          onDeactivate={(tokenToDeactivate: RfidToken) => {
            handleOpenDeactivate(tokenToDeactivate);
          }}
          onRecharge={(groupToRecharge: CardPool) => {
            handleOpenRecharge(groupToRecharge);
          }}
          onUpdate={(updatedToken?: RfidToken) => {
            if (updatedToken) {
              setActiveToken(updatedToken);
            }
            dispatch(fetchTokensApi({ page: tokensPage, limit: tokensLimit }));
            dispatch(fetchCardPoolsApi({ page: cardPoolsPage, limit: cardPoolsLimit }));
            dispatch(fetchRfidSummaryApi());
          }}
        />
      );
    }
    if (sidePanelType === 'group-details' && activeGroup) {
      const groupId = activeGroup._id || activeGroup.id;
      return (
        <GroupDetailsModal
          key={`group-details-${groupId}-${sidePanelRefreshKey}`}
          group={activeGroup}
          groupId={groupId}
          isEditing={isGroupEditing}
          onEditModeChange={setIsGroupEditing}
          onClose={handleCloseSidePanel}
          onRecharge={(groupToRecharge: CardPool) => {
            handleOpenRecharge(groupToRecharge);
          }}
          onUpdate={(updatedGroup?: CardPool) => {
            if (updatedGroup) {
              setActiveGroup(updatedGroup);
            }
            dispatch(fetchCardPoolsApi({ page: cardPoolsPage, limit: cardPoolsLimit }));
            dispatch(fetchTokensApi({ page: tokensPage, limit: tokensLimit }));
            dispatch(fetchRfidSummaryApi());
          }}
        />
      );
    }
    if (sidePanelType === 'ledger-details' && activeLedgerEntry) {
      const ledgerId = activeLedgerEntry._id || activeLedgerEntry.id;
      return (
        <LedgerDetailsModal
          key={`ledger-details-${ledgerId}`}
          entry={activeLedgerEntry}
          ledgerId={ledgerId}
          onClose={handleCloseSidePanel}
        />
      );
    }
    return undefined;
  };

  const renderRightTopActions = (
    <RfidTableActions onOpenAddCard={handleOpenAddCard} onOpenAddGroup={handleOpenAddGroup} />
  );

  return (
    <div className="rfid-management-page" dir={i18n.dir()}>
      {/* ── Quick Statistics Overview Cards ────────────────── */}
      <RfidStatsOverview />

      {/* ── Tab Navigation ─────────────────────────────────── */}
      <TabToggle
        tabs={tabs}
        defaultTab={String(activeTab)}
        isLabelValue
        onChange={(selectedTab) => {
          handleCloseSidePanel();
          setActiveTab(Number(selectedTab));
        }}
      />

      {/* ── Tab Views with CustomTable ─────────────────────── */}
      {activeTab === 0 && (
        <TokensList
          rightTopContent={renderRightTopActions}
          onOpenAddModal={handleOpenAddCard}
          onOpenDetailsModal={handleOpenTokenDetails}
          onOpenEditModal={handleOpenEditToken}
          onOpenDeactivateModal={handleOpenDeactivate}
          onOpenRechargeModal={handleOpenRecharge}
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={renderSidePanelManualContent()}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent()}
          onSidePanelClose={handleCloseSidePanel}
        />
      )}

      {activeTab === 1 && (
        <CardGroupsList
          rightTopContent={renderRightTopActions}
          onOpenAddModal={handleOpenAddGroup}
          onOpenRechargeModal={handleOpenRecharge}
          onOpenDetailsModal={handleOpenGroupDetails}
          onOpenEditModal={handleOpenEditGroup}
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={renderSidePanelManualContent()}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent()}
          onSidePanelClose={handleCloseSidePanel}
        />
      )}

      {activeTab === 2 && (
        <LedgerEntriesList
          rightTopContent={renderRightTopActions}
          onOpenDetailsModal={handleOpenLedgerDetails}
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={renderSidePanelManualContent()}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent()}
          onSidePanelClose={handleCloseSidePanel}
        />
      )}

      {/* ── Modals ─────────────────────────────────────────── */}
      {successModalData.open && (
        <ModalBox
          open={successModalData.open}
          title=""
          header={false}
          notification
          closeModal={() => setSuccessModalData((previous) => ({ ...previous, open: false }))}
          content={
            <SuccessNotification
              title={successModalData.title}
              description={successModalData.description}
              notificationCloseTimer={3000}
              onNotificationClose={() =>
                setSuccessModalData((previous) => ({ ...previous, open: false }))
              }
            />
          }
        />
      )}

      {rechargeGroup && (
        <ModalBox
          open={Boolean(rechargeGroup)}
          title={
            rechargeGroup.kind === 'INDIVIDUAL'
              ? t('rechargeCardBalance', { defaultValue: 'Recharge Card Energy Balance (kWh)' })
              : t('rechargeCardGroup', { defaultValue: 'Recharge Card Group Balance (kWh)' })
          }
          header
          closeModal={() => setRechargeGroup(null)}
          content={
            <RechargeGroupModal
              group={rechargeGroup}
              onClose={() => setRechargeGroup(null)}
              onSuccess={handleRechargeSuccess}
            />
          }
        />
      )}

      {deactivateTargetToken && (
        <ModalBox
          open={Boolean(deactivateTargetToken)}
          title=""
          header={false}
          notification
          closeModal={() => setDeactivateTargetToken(null)}
          content={
            <DeactivateCardModal
              token={deactivateTargetToken}
              onClose={() => setDeactivateTargetToken(null)}
              onSuccess={handleDeactivateSuccess}
            />
          }
        />
      )}
    </div>
  );
}

export default RFIDManagement;
